import { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, CheckCircle2, Bell, Package } from "lucide-react";

export default function InsightsCard() {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/stores/me/insights"); setData(r.data); } catch (e) { /* ignore */ }
      try { const r = await api.get("/stores/me/analytics"); setSeries(r.data); } catch (e) { /* ignore */ }
    })();
  }, []);
  if (!data) return null;
  const Stat = ({ icon: Icon, label, value, tint }) => (
    <div className={`rounded-xl border p-3 ${tint}`}>
      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest opacity-70"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
  return (
    <Card className="border-slate-200" data-testid="insights-card">
      <CardHeader className="pb-3"><CardTitle className="font-display text-xl">Insights</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Stat icon={Eye} label="Views" value={data.views} tint="bg-emerald-50 border-emerald-200 text-emerald-900" />
          <Stat icon={CheckCircle2} label="Redemptions" value={data.redemptions} tint="bg-amber-50 border-amber-200 text-amber-900" />
          <Stat icon={Bell} label="Followers" value={data.followers} tint="bg-white border-slate-200 text-slate-900" />
          <Stat icon={Package} label="Active listings" value={data.active} tint="bg-white border-slate-200 text-slate-900" />
        </div>
        {data.top?.length > 0 && (
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">Top performers</div>
            {data.top.map(t => (
              <div key={t.id} className="flex items-center gap-2 py-1.5 border-t border-slate-100 first:border-t-0" data-testid={`top-${t.id}`}>
                <img alt="" src={imageUrl(t.image_path)} className="w-8 h-8 rounded object-cover bg-slate-100" />
                <div className="flex-1 min-w-0 text-sm truncate">{t.product_name}</div>
                <div className="text-[11px] text-slate-500"><span className="text-emerald-800 font-bold">{t.views}</span> views · <span className="text-amber-700 font-bold">{t.redemptions}</span> ✓</div>
              </div>
            ))}
          </div>
        )}
        {series?.series?.length > 0 && <Sparkline series={series.series} />}
      </CardContent>
    </Card>
  );
}

function Sparkline({ series }) {
  const w = 260, h = 60, pad = 4;
  const vals = series.map(x => x.views);
  const reds = series.map(x => x.redemptions);
  const max = Math.max(1, ...vals, ...reds);
  const step = (w - pad * 2) / Math.max(1, series.length - 1);
  const y = v => h - pad - (v / max) * (h - pad * 2);
  const path = arr => arr.map((v, i) => `${i === 0 ? "M" : "L"}${(pad + i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <div className="pt-2 border-t border-slate-100" data-testid="analytics-sparkline">
      <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">Last 14 days</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16">
        <path d={path(vals)} fill="none" stroke="#065f46" strokeWidth="1.8" />
        <path d={path(reds)} fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeDasharray="3 3" />
      </svg>
      <div className="flex gap-3 text-[10px] text-slate-500">
        <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-800 mr-1" />Views</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />Redemptions</span>
      </div>
    </div>
  );
}
